import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import {
    requestByConnection,
    RequestDto,
    ResponseDto,
} from '@r1-frontend/api-domru/api-request/v1/user/request-by-connection';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { PolicyNote } from '@r1-frontend/ui-react/components/PolicyNote';
import { EResultPopupStatus } from '@r1-frontend/ui-react/components/popups/popupResult';
import { IndentContainer } from '@r1-frontend/ui-react/experimental/containers';
import { EProductsId } from '@r1-frontend/shared/types/EProductsId';

import { fullBuyEquipAnalyticsBase } from '~/src/analytics';
import type { TField } from '~/src/components/RequestForm';
import { POLICY_PAGE_LINK } from '~/src/constants';
import type { IPopupProps } from '~/src/features/router/order-card';
import type { IProps } from '~/src/features/yandex-landing/InputBlock/types';
import { selectCsrf } from '~/src/store/selectors/auth/selectCsrf';

import * as ST from './styled';

const getPopupResultData = ({ result, message } : { result: boolean, message: string }): Omit<IPopupProps, 'isOrderOpened'> => {
    return {
        isResultOpened: true,
        resultType: result ? EResultPopupStatus.SUCCESS : EResultPopupStatus.ERROR,
        resultTitle: result ? 'Заявка отправлена' : 'Произошла ошибка',
        resultText: message || (result ? 'Мы свяжемся с вами в ближайшее время' : 'Что-то пошло не так, попробуйте позже.'),
    };
};

const InputBlock: React.FC<IProps> = ({
    setPopup,
    setIsLoading,
    phone,
    name,
    setPhone,
    setName,
    isValid,
    withoutName,
    withoutAgreement,
    InputWrapperComponent,
    InputNameComponent,
    InputPhoneComponent,
    ButtonWrapperComponent,
    ButtonComponent,
    buttonTitle,
    namePlaceholder,
    buttonType,
    agreeAboveButton,
    options,
}) => {
    const csrf = useSelector(selectCsrf);
    const [beenStarted, setDataLayer] = useState(false);

    const onChangeHandlerInput = useCallback((stateInput: TField, _setState: Dispatch<SetStateAction<TField>>) => {
        if (!beenStarted && stateInput.value) {
            setDataLayer(true);
        }
        _setState(stateInput);
    }, [beenStarted, options]);

    const openPopupResult = useCallback((result: boolean, message: string) => {
        if (options?.name) {
            fullBuyEquipAnalyticsBase.applicationForm(options?.name);
        }
        setPopup({ ...getPopupResultData({ result, message }) });
    }, [setPopup, options]);

    const onSubmit = useCallback(async() => {
        setIsLoading(true);

        let params: RequestDto = {
            fio: name.value || 'инкогнито',
            phone: phone.value,
            product_id: [EProductsId.internet],
            csrf,
        };

        if (options) {
            params = {
                ...params,
                cost_type: options.type ? options.type : 5,
                internet_equipment_id: options.internet_equipment_id,
                leasing_duration: options.value,
            };
        }

        const resp = await doRequest.apiRequest<ResponseDto>(requestByConnection(params));
        if (resp.isSuccess) {
            const { status, message } = resp.payload;
            const ok = !!status;

            openPopupResult(ok, message);
        } else {
            openPopupResult(false, 'Не удалось отправить заявку. Пожалуйста, попробуйте еще раз.');
        }
        setIsLoading(false);
    }, [name.value, phone.value, options]);

    const onChangeName = useCallback((stateInput: TField) => {
        onChangeHandlerInput(stateInput, setName);
    }, [onChangeHandlerInput, setName]);

    const onChangePhone = useCallback((stateInput: TField) => {
        onChangeHandlerInput(stateInput, setPhone);
    }, [onChangeHandlerInput, setPhone]);

    const InputWrapper = useMemo(() => {
        return InputWrapperComponent || React.Fragment;
    }, [InputWrapperComponent]);

    const InputName = useMemo(() => {
        return InputNameComponent || ST.InputName;
    }, [InputNameComponent]);

    const InputPhone = useMemo(() => {
        return InputPhoneComponent || ST.InputPhone;
    }, [InputPhoneComponent]);

    const ButtonWrapper = useMemo(() => {
        return ButtonWrapperComponent || React.Fragment;
    }, [ButtonWrapperComponent]);

    const CallbackButton = useMemo(() => {
        return ButtonComponent || ST.CallbackButton;
    }, [ButtonComponent]);

    const buttonText = buttonTitle || 'Перезвоните мне';

    return (
        <InputWrapper>
            {!withoutName && <InputName
                // @ts-ignore
                onChange={onChangeName}
                type='name'
                errorMessage='Должны быть только буквы'
                placeholder={namePlaceholder || 'Как к вам обращаться?'}
                name='name'
                className={'ym-disable-keys'}
            />}
            <InputPhone
                // @ts-ignore
                onChange={onChangePhone}
                type='phone'
                errorMessage='Укажите корректный номер телефона'
                placeholder='Номер телефона'
                name='phone'
                className={'ym-disable-keys'}
            />
            <ButtonWrapper>
                {!agreeAboveButton && <CallbackButton
                    // @ts-ignore
                    type={buttonType || btnTypes.ACCENT}
                    text={buttonText}
                    onClick={onSubmit}
                    disabled={!isValid}
                />}
                {!withoutAgreement &&
                    <IndentContainer $united={[0, 0, 12, 0]}>
                        <PolicyNote
                            buttonText={buttonText}
                            agreeUrl={POLICY_PAGE_LINK}
                        />
                    </IndentContainer>}
                {!!agreeAboveButton && <ST.DoubleButtonComponent>
                    <CallbackButton
                        // @ts-ignore
                        type={buttonType || btnTypes.ACCENT}
                        text={buttonTitle || 'Перезвоните мне'}
                        onClick={onSubmit}
                        disabled={!isValid}
                        style={{
                            display: 'flex',
                            flex: 1,
                        }}
                    />
                </ST.DoubleButtonComponent>}
            </ButtonWrapper>
        </InputWrapper>
    );
};

export default InputBlock;
